import { useState, useEffect } from 'react';

import WorldMap from "./WorldMap";

import './Reports.css';
import './General.css';

import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie } from "recharts";

import { GetDataPage, RegisterDataPage, UnRegisterDataPage } from '../../interface/Data';
import { SetStatusGlobal } from '../../interface/Global';

import Comp_Select from '../../components/Select';
import type { SelectOption } from '../../components/Select';

import { AnalyticsAge, AnalyticsCountryUsers, AnalyticsGender, AnalyticsLanguage, AnalyticsRealtime, GetDataGeneral } from './Analytics';
import { ProjectNamesByCompany, ReportClientMap, ReportProjectMap, ShowClientList } from './Filters';

const ACCENT = "#EEA23F";

interface GenderChart {
    name  : string;
    value : number;
    fill  : string;
}

const GENDER_MAP: { [key: string]: { name: string; fill: string } } = {
    male:   { name: "Male",   fill: ACCENT      },
    female: { name: "Female", fill: "#ffffff33" },
};

const defaultGender: GenderChart[] = [
    { name: "Male",   value: 57.1, fill: ACCENT      },
    { name: "Female", value: 42.9, fill: "#ffffff33" },
];

const dataRealtimePerMinute = [
    { min: "1", users: 0 },
    { min: "2", users: 0 },
    { min: "3", users: 1 },
    { min: "4", users: 0 },
    { min: "5", users: 2 },
    { min: "6", users: 0 },
    { min: "7", users: 1 },
    { min: "8", users: 3 },
    { min: "9", users: 0 },
    { min: "10", users: 2 },
];

const dataTopCountriesRealtime: AnalyticsCountryUsers[] = [
    { country: "Brazil", users: 2 },
];

const dataAge: AnalyticsAge[] = [
    { age: "25-34", users: 100 },
    { age: "35-44", users: 65 },
    { age: "18-24", users: 40 },
    { age: "45-54", users: 28 },
];

// linha da tabela: o país vem em "country" e a cidade em "city", por isso a
// coluna é escolhida por parâmetro
interface TableRow {
    [key: string] : string | number;
}

interface CardHeaderProps {
    title : string;
    sub?  : string;
}

interface TableRowsProps {
    data : TableRow[];
    colA : string;
    colB : string;
}

function CardHeader({ title, sub }: CardHeaderProps) {
    return (
        <div className="rp-card-header">
            <span className="rp-card-title">{ title }</span>
            {
                sub && <span className="rp-card-sub">{ sub }</span>
            }
        </div>
    );
}

function TableRows({ data, colA, colB }: TableRowsProps) {
    const maxVal = Math.max(...data.map(d => Number(d[colB])));
    return (
        <div className="rp-table">
            <div className="rp-table-head">
                <span>{ colA.toUpperCase() }</span>
                <span>ACTIVE USERS</span>
            </div>
            {
                data.map((row, i) => (
                    <div className="rp-table-row" key={i}>
                        <span className="rp-table-label">{ row[colA] }</span>
                        <span className="rp-table-val">{ row[colB] }</span>
                        <div className="rp-table-bar-bg">
                            <div className="rp-table-bar-fill" style={ { width: `${(Number(row[colB]) / maxVal) * 100}%` } } />
                        </div>
                    </div>
                ))
            }
        </div>
    );
}

function CardCountry({ data }: { data: AnalyticsCountryUsers[] }) {
    return (
        <div className="rp-card rp-card-wide">
            <CardHeader title="Active users" sub="by Country" />
            <WorldMap data={ data } />
            <TableRows data={ data as unknown as TableRow[] } colA="country" colB="users" />
            {/* <div className="rp-card-link">View countries →</div> */}
        </div>
    );
}

function CardRealtime({ data, showGlobalNote }: { data: AnalyticsRealtime | null; showGlobalNote: boolean }) {
    const activeUsers  = data?.active_users  ?? 0;
    const perMinute    = data?.per_minute    ?? dataRealtimePerMinute;
    const topCountries = data?.top_countries ?? dataTopCountriesRealtime;
    const [ showTooltip, setShowTooltip ] = useState(false);

    return (
        <div className="rp-card">
            <CardHeader title="ACTIVE USERS IN LAST 30 MINUTES" />
            <div className="rp-realtime-count">{ activeUsers }</div>
            <div className="rp-realtime-label">ACTIVE USERS PER MINUTE</div>
            <div onMouseEnter={ () => setShowTooltip(true) } onMouseLeave={ () => setShowTooltip(false) }>
                <ResponsiveContainer width="100%" height={60}>
                    <BarChart data={ perMinute } margin={ { top: 0, right: 0, left: 0, bottom: 0 } }>
                        { showTooltip && (
                            <Tooltip
                                contentStyle={ { background: "#1a1a2e", border: "none", color: "#fff", fontSize: 11 } }
                                cursor={ { fill: "rgba(255,255,255,0.05)" } }
                                formatter={ (value: any) => [value, "Users"] }
                                labelFormatter={ (label: any) => `Min ${label}` }
                            />
                        ) }
                        <Bar dataKey="users" fill={ ACCENT } radius={ [2, 2, 0, 0] } />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="rp-table rp-mt">
                <div className="rp-table-head">
                    <span>TOP COUNTRIES</span>
                    <span>ACTIVE USERS</span>
                </div>
                {
                    topCountries.map((row, i) => (
                        <div className="rp-table-row" key={i}>
                            <span className="rp-table-label">{ row.country }</span>
                            <span className="rp-table-val">{ row.users }</span>
                        </div>
                    ))
                }
            </div>
            {/* <div className="rp-card-link">View realtime →</div> */}
            {
                showGlobalNote ?
                    <div className="rp-card-link">
                        The realtime report always covers the whole platform, so this card does not follow the selected client.
                    </div>
                : null
            }
        </div>
    );
}

function CardCity({ data }: { data: TableRow[] }) {
    return (
        <div className="rp-card">
            <CardHeader title="Active users" sub="by City" />
            <TableRows data={ data } colA="city" colB="users" />
            {/* <div className="rp-card-link">View cities →</div> */}
        </div>
    );
}

function CardGender({ data }: { data: AnalyticsGender[] }) {
    const unknown = data?.filter(d => d.gender === 'unknown').reduce((sum, d) => sum + d.users, 0) ?? 0;
    const identified = data?.filter(d => d.gender !== 'unknown').reduce((sum, d) => sum + d.users, 0) ?? 0;

    const grandTotal = unknown + identified;
    const totalResult = grandTotal > 0
        ? parseFloat(((identified / grandTotal) * 100).toFixed(1))
        : 0;

    const filtered = data?.filter(d => d.gender !== 'unknown') ?? [];
    const filteredTotal = filtered.reduce((sum, d) => sum + d.percent, 0);
    const chartData: GenderChart[] = filtered.length > 0
        ? filtered.map(d => ({
            name:  (GENDER_MAP[d.gender] ?? GENDER_MAP.male).name,
            value: filteredTotal > 0 ? parseFloat(((d.percent / filteredTotal) * 100).toFixed(1)) : d.percent,
            fill:  (GENDER_MAP[d.gender] ?? GENDER_MAP.male).fill,
          }))
        : defaultGender;

    return (
        <div className="rp-card">
            <CardHeader title="Active users" sub="by Gender" />
            <div className="rp-donut-wrap">
                <ResponsiveContainer width="100%" height={ 180 }>
                    <PieChart>
                        <Pie
                            data={ chartData }
                            cx="50%"
                            cy="50%"
                            innerRadius={ 55 }
                            outerRadius={ 80 }
                            dataKey="value"
                            startAngle={ 90 }
                            endAngle={ -270 }
                        />
                    </PieChart>
                </ResponsiveContainer>
                <div className="rp-donut-legend">
                    {
                        chartData.map((d, i) => (
                            <div className="rp-donut-legend-item" key={ i }>
                                <span className="rp-dot" style={ { background: d.fill } } />
                                <span className="rp-legend-label">{ d.name.toUpperCase() }</span>
                                <span className="rp-legend-val">{ d.value }%</span>
                            </div>
                        ))
                    }
                </div>
            </div>
            {/* <div className="rp-card-link">View genders →</div> */}
            <div className="rp-card-link">
                These percentages are estimated based on approximately { totalResult }% of total users, considering only those who accepted cookies and were logged into a Google account while accessing the platform.
            </div>
        </div>
    );
}

function CardLanguage({ data }: { data: AnalyticsLanguage[] }) {
    const chartData: AnalyticsLanguage[] = data && data.length > 0 ? data.slice(0, 8) : [
        { language: "English",    users: 700 },
        { language: "Portuguese", users: 80  },
        { language: "Russian",    users: 45  },
        { language: "Arabic",     users: 30  },
        { language: "German",     users: 20  },
        { language: "French",     users: 18  },
        { language: "Turkish",    users: 10  },
    ];

    return (
        <div className="rp-card">
            <CardHeader title="Active users" sub="by Language" />
            <ResponsiveContainer width="100%" height={ chartData.length * 40 }>
                <BarChart data={ chartData } layout="vertical" margin={ { top: 5, right: 20, left: 10, bottom: 5 } }>
                    <XAxis type="number" tick={ { fill: "#fff", fontSize: 11 } } axisLine={ false } tickLine={ false } />
                    <YAxis type="category" dataKey="language" tick={ { fill: "#fff", fontSize: 11 } } axisLine={ false } tickLine={ false } width={ 80 } />
                    <Tooltip contentStyle={ { background: "#1a1a2e", border: "none", color: "#fff" } } cursor={ { fill: "rgba(255,255,255,0.05)" } } />
                    <Bar dataKey="users" fill={ ACCENT } radius={ [0, 3, 3, 0] } barSize={ 20 } />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function CardAge({ data }: { data: AnalyticsAge[] }) {
    const unknown    = data?.find(d => d.age === 'unknown')?.users ?? 0;
    const identified = data?.filter(d => d.age !== 'unknown').reduce((sum, d) => sum + d.users, 0) ?? 0;
    const grandTotal = unknown + identified;
    const totalResult = grandTotal > 0
        ? parseFloat(((identified / grandTotal) * 100).toFixed(1))
        : 0;

    const chartData = data && data.filter(d => d.age !== 'unknown').length > 0
        ? data.filter(d => d.age !== 'unknown')
        : dataAge;

    return (
        <div className="rp-card">
            <CardHeader title="Active users" sub="by Age" />
            <ResponsiveContainer width="100%" height={ 180 }>
                <BarChart data={ chartData } layout="vertical" margin={ { top: 5, right: 20, left: 10, bottom: 5 } }>
                    <XAxis type="number" tick={ { fill: "#fff", fontSize: 11 } } axisLine={ false } tickLine={ false } />
                    <YAxis type="category" dataKey="age" tick={ { fill: "#fff", fontSize: 11 } } axisLine={ false } tickLine={ false } width={ 40 } />
                    <Tooltip contentStyle={ { background: "#1a1a2e", border: "none", color: "#fff" } } cursor={{ fill: "rgba(255,255,255,0.05)" }} />
                    <Bar dataKey="users" fill={ ACCENT } radius={ [0, 3, 3, 0] } />
                </BarChart>
            </ResponsiveContainer>
            {/* <div className="rp-card-link">View age ranges →</div> */}
            <div className="rp-card-link">
                These percentages are estimated based on approximately { totalResult }% of total users, considering only those who accepted cookies and were logged into a Google account while accessing the platform.
            </div>
        </div>
    );
}

export default function Page_Reports_General() {

    const [ listClients, setListClients ] = useState<ReportClientMap>(GetDataPage('clients') || {});
    const [ listProjects, setListProjects ] = useState<ReportProjectMap>(GetDataPage('projects') || {});

    const [ company, setCompany ] = useState<SelectOption<number>>({ "value": 0, "label": "All clients", "qtd_project": 0 });

    const [ dataCountry, setDataCountry ] = useState<AnalyticsCountryUsers[]>([]);
    const [ dataRealtime, setDataRealtime ] = useState<AnalyticsRealtime | null>(null);
    const [ dataCity, setDataCity ] = useState<TableRow[]>([]);
    const [ dataGenderState, setDataGenderState ] = useState<AnalyticsGender[]>([]);
    const [ dataLanguageState, setDataLanguageState ] = useState<AnalyticsLanguage[]>([]);
    const [ dataAgeState, setDataAgeState ] = useState<AnalyticsAge[]>([]);

    function ClearData(){
        setDataCountry([]);
        setDataCity([]);
        setDataGenderState([]);
        setDataLanguageState([]);
        setDataAgeState([]);
    }

    function ShowDataAnalytics(){
        SetStatusGlobal('loading', true);

        // o Analytics só conhece o projeto pelo nome, então o cliente vira a
        // lista de projetos dele
        const projectNames = company.value ? ProjectNamesByCompany(listProjects, company.value) : [];

        // cliente escolhido que ainda não tem projeto cadastrado: sem nome para
        // filtrar, o PHP devolveria a plataforma inteira, então só o realtime
        // (que é global de qualquer jeito) é aproveitado
        const onlyRealtime = company.value !== 0 && projectNames.length === 0;

        GetDataGeneral(projectNames)
        .then(response => {
            if (response.realtime) {
                setDataRealtime(response.realtime);
            }

            if (onlyRealtime) {
                ClearData();
                SetStatusGlobal('loading', false);
                return;
            }

            if (response.location) {
                setDataCountry([...response.location].sort((a, b) => b.users - a.users).slice(0, 7));
            }
            if (response.location) {
                setDataCity(
                    response.location
                        .flatMap(loc => loc.regions ?? [])
                        .flatMap(r => r.cities ?? [])
                        .sort((a, b) => b.users - a.users)
                        .slice(0, 13) as unknown as TableRow[]
                );
            }
            if (response.gender) {
                setDataGenderState(response.gender);
            }
            if (response.languages) {
                setDataLanguageState(response.languages);
            }
            if (response.age_brackets) {
                setDataAgeState(response.age_brackets.map(d => ({ age: d.age_bracket, users: d.users })));
            }
            SetStatusGlobal('loading', false);
        })
        .catch(error => {
            SetStatusGlobal('loading', false);
            console.error("Erro ao carregar Analytics:", error);
        });
    }

    useEffect(() => {
        RegisterDataPage('clients', setListClients);
        RegisterDataPage('projects', setListProjects);
        return ()=>{
            UnRegisterDataPage('clients', setListClients);
            UnRegisterDataPage('projects', setListProjects);
        };
    }, []);

    // cada troca de cliente é uma consulta nova, porque o filtro é aplicado
    // direto na chamada do Analytics
    useEffect(() => {
        ShowDataAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company.value]);

    return(
        <div className="page_report page_general">
            <div className="div_list_client margin_bottom">
                <Comp_Select title="Client" list={ ShowClientList(listClients) } value={ company } setValue={ setCompany } required={ true } />
            </div>

            <div className="rp-grid">
                <CardCountry data={ dataCountry } />
                <CardRealtime data={ dataRealtime } showGlobalNote={ company.value !== 0 } />
                <CardCity data={ dataCity } />

                <CardGender data={ dataGenderState } />
                <CardLanguage data={ dataLanguageState } />
                <CardAge data={ dataAgeState } />
            </div>
        </div>
    );
}
